import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar } from "../components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, 
  Link2, 
  Copy,
  Send,
  Calendar,
  Users,
  User,
  ExternalLink,
  Clock,
  CheckCircle2,
  Mail,
  Globe,
  Briefcase,
  Trash2,
  ArrowUpDown
} from "lucide-react";
import { toast } from "sonner";
import { interviewLanguages } from "../i18n";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InterviewLinks() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [links, setLinks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [linkType, setLinkType] = useState('individual');
  const [sendEmail, setSendEmail] = useState(true);
  const [createdLink, setCreatedLink] = useState(null);
  
  // Interview language settings
  const [isBilingual, setIsBilingual] = useState(false);
  const [primaryLanguage, setPrimaryLanguage] = useState('fr');
  const [secondaryLanguage, setSecondaryLanguage] = useState('en');
  
  // Link validity settings
  const [linkValidity, setLinkValidity] = useState('7d'); // 24h, 7d, 30d
  
  // Sorting
  const [sortLinkKey, setSortLinkKey] = useState("created_at");
  const [sortLinkDir, setSortLinkDir] = useState("desc");

  const toggleLinkSort = (key) => {
    if (sortLinkKey === key) setSortLinkDir(d => d === "asc" ? "desc" : "asc");
    else { setSortLinkKey(key); setSortLinkDir("asc"); }
  };

  const sortedLinks = useMemo(() => {
    return [...links].sort((a, b) => {
      let va, vb;
      if (sortLinkKey === "job_title") {
        va = getJobTitle(a.job_id); vb = getJobTitle(b.job_id);
      } else {
        va = a[sortLinkKey] ?? ""; vb = b[sortLinkKey] ?? "";
      }
      return sortLinkDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [links, sortLinkKey, sortLinkDir]);
  
  // Navigate for credit redirect
  const navigate = useNavigate();
  
  // Get difficulty level from selected job (read-only, inherited from job)
  const selectedJobData = useMemo(() => {
    if (!selectedJob) return null;
    return jobs.find(j => j.job_id === selectedJob);
  }, [selectedJob, jobs]);
  
  const difficultyLevel = selectedJobData?.difficulty_level || 'medium';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, linksRes, jobsRes, candidatesRes] = await Promise.all([
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
        fetch(`${API}/api/interview-links`, { credentials: 'include' }),
        fetch(`${API}/api/jobs`, { credentials: 'include' }),
        fetch(`${API}/api/candidates`, { credentials: 'include' })
      ]);

      if (userRes.ok) setUser(await userRes.json());
      if (linksRes.ok) setLinks(await linksRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
      if (candidatesRes.ok) setCandidates(await candidatesRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async () => {
    if (!selectedJob || selectedCandidates.length === 0) {
      toast.error(t('interviewLinks.form.selectJob', "Sélectionnez un poste et au moins un candidat"));
      return;
    }

    try {
      const response = await fetch(`${API}/api/interview-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_id: selectedJob,
          candidate_ids: selectedCandidates,
          link_type: linkType,
          send_email: sendEmail,
          interview_language: primaryLanguage,
          is_bilingual: isBilingual,
          secondary_language: isBilingual ? secondaryLanguage : null,
          validity_period: linkValidity
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCreatedLink(result);
        
        if (sendEmail && result.emails_sent?.length > 0) {
          toast.success(`Lien créé et ${result.emails_sent.length} email(s) envoyé(s) !`);
        } else {
          toast.success("Lien d'entretien créé !");
        }
        
        fetchData();
      } else if (response.status === 402) {
        // Insufficient credits
        const error = await response.json();
        toast.error(error.detail?.message || "Crédits insuffisants");
        // Redirect to billing after a short delay
        setTimeout(() => {
          navigate('/billing');
        }, 1500);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const resetForm = () => {
    setSelectedJob('');
    setSelectedCandidates([]);
    setLinkType('individual');
    setSendEmail(true);
    setCreatedLink(null);
    setLinkValidity('7d');
    setIsBilingual(false);
    setPrimaryLanguage('fr');
    setSecondaryLanguage('en');
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/interview/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Lien copié !");
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce lien ?")) return;
    
    try {
      const response = await fetch(`${API}/api/interview-links/${linkId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Lien supprimé !");
        fetchData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resendEmailToCandidate = async (linkId) => {
    try {
      const response = await fetch(`${API}/api/email/send-interview-link?link_id=${linkId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Emails envoyés !");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const getJobTitle = (jobId) => {
    const job = jobs.find(j => j.job_id === jobId);
    return job?.title || 'N/A';
  };

  const getCandidateName = (candidateId) => {
    const candidate = candidates.find(c => c.candidate_id === candidateId);
    return candidate?.name || 'N/A';
  };

  const filteredCandidates = selectedJob 
    ? candidates.filter(c => c.job_id === selectedJob && c.status !== 'hired' && c.status !== 'rejected')
    : [];

  const toggleCandidate = (candidateId) => {
    setSelectedCandidates(prev => 
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  return (
    <div className="min-h-screen bg-canvas dark:bg-zinc-950">
      <Navbar user={user} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-ink dark:text-zinc-100">{t('interviewLinks.title')}</h1>
            <p className="text-ink-muted mt-1">{t('interviewLinks.subtitle')}</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-link-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('interviewLinks.newLink', 'Nouveau lien')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{t('interviewLinks.createTitle', 'Créer un lien d\'entretien')}</DialogTitle>
                <DialogDescription>
                  Sélectionnez un poste et les candidats pour générer un lien d'entretien.
                  Les crédits ne sont consommés que lorsque l'entretien est complété.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Credits reminder */}
                <div className="bg-brand-accent/10 border border-brand-accent/30 rounded-lg p-4">
                  <p className="text-sm text-brand-primary">
                    <span className="font-semibold">Crédits disponibles:</span> {user?.interview_credits || 0}
                  </p>
                </div>

                {/* Candidate Selection - Grouped by Job (no separate job selector) */}
                <div className="space-y-3">
                  <label className="text-sm font-medium">Sélectionnez le(s) candidat(s) *</label>
                  <p className="text-xs text-ink-muted mb-2">Les candidats sont organisés par poste</p>
                  
                  <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                    {jobs.length === 0 ? (
                      <p className="p-4 text-sm text-ink-muted text-center">
                        Aucun poste créé. Créez d'abord un poste.
                      </p>
                    ) : (
                      jobs.map(job => {
                        const jobCandidates = candidates.filter(c => 
                          c.job_id === job.job_id && 
                          c.status !== 'hired' && 
                          c.status !== 'rejected' &&
                          c.status !== 'interviewed'
                        );
                        
                        if (jobCandidates.length === 0) return null;
                        
                        return (
                          <div key={job.job_id} className="border-b last:border-b-0">
                            {/* Job Header */}
                            <div className="bg-canvas-paper px-4 py-2 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Briefcase className="w-4 h-4 text-brand-primary" />
                                <span className="font-medium text-sm">{job.title}</span>
                                <Badge variant="outline" className="text-xs">
                                  {job.company_name || job.department}
                                </Badge>
                              </div>
                              <span className="text-xs text-ink-muted">
                                {jobCandidates.length} candidat(s)
                              </span>
                            </div>
                            
                            {/* Candidates for this job */}
                            {jobCandidates.map(candidate => (
                              <div 
                                key={candidate.candidate_id}
                                className="flex items-center gap-3 p-3 hover:bg-canvas-paper cursor-pointer border-t"
                                onClick={() => {
                                  toggleCandidate(candidate.candidate_id);
                                  // Auto-select the job when selecting a candidate
                                  if (!selectedCandidates.includes(candidate.candidate_id)) {
                                    setSelectedJob(job.job_id);
                                  }
                                }}
                              >
                                <Checkbox 
                                  checked={selectedCandidates.includes(candidate.candidate_id)}
                                  onCheckedChange={() => {
                                    toggleCandidate(candidate.candidate_id);
                                    if (!selectedCandidates.includes(candidate.candidate_id)) {
                                      setSelectedJob(job.job_id);
                                    }
                                  }}
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{candidate.name}</p>
                                  <p className="text-xs text-ink-muted">{candidate.email}</p>
                                </div>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {candidate.status === 'new' ? 'Nouveau' : candidate.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {selectedCandidates.length > 0 && (
                    <p className="text-sm text-brand-primary font-medium">
                      {selectedCandidates.length} candidat(s) sélectionné(s)
                    </p>
                  )}
                </div>

                {/* Link Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type de lien</label>
                  <Select value={linkType} onValueChange={setLinkType}>
                    <SelectTrigger data-testid="link-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Individuel (1 candidat par lien)
                        </div>
                      </SelectItem>
                      <SelectItem value="group">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Groupe (plusieurs candidats)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Auto-send email option */}
                <div className="flex items-center justify-between p-4 bg-canvas-paper rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-brand-primary" />
                    <div>
                      <p className="text-sm font-medium">{t('interviewLinks.form.sendEmail', 'Envoyer automatiquement par email')}</p>
                      <p className="text-xs text-ink-muted">Le candidat recevra une invitation personnalisée</p>
                    </div>
                  </div>
                  <Switch 
                    checked={sendEmail} 
                    onCheckedChange={setSendEmail}
                    data-testid="send-email-switch"
                  />
                </div>

                {/* Interview Language Selection */}
                <div className="space-y-4 p-4 bg-canvas-paper rounded-lg">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-brand-primary" />
                    <div>
                      <p className="text-sm font-medium">{t('interviewLanguages.title', "Langue de l'entretien")}</p>
                      <p className="text-xs text-ink-muted">Choisissez la langue dans laquelle l'IA mènera l'entretien</p>
                    </div>
                  </div>
                  
                  {/* Bilingual Toggle */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor="bilingual-switch" className="text-sm">
                      {t('interviewLanguages.bilingual', 'Mode bilingue')}
                    </Label>
                    <Switch 
                      id="bilingual-switch"
                      checked={isBilingual} 
                      onCheckedChange={setIsBilingual}
                      data-testid="bilingual-switch"
                    />
                  </div>

                  {/* Primary Language */}
                  <div className="space-y-2">
                    <Label className="text-sm">
                      {isBilingual 
                        ? t('interviewLanguages.selectPrimary', 'Langue principale') 
                        : t('interviewLanguages.title', "Langue de l'entretien")}
                    </Label>
                    <Select value={primaryLanguage} onValueChange={setPrimaryLanguage}>
                      <SelectTrigger data-testid="primary-language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {interviewLanguages.map(lang => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Secondary Language (only if bilingual) */}
                  {isBilingual && (
                    <div className="space-y-2">
                      <Label className="text-sm">
                        {t('interviewLanguages.selectSecondary', 'Langue secondaire')}
                      </Label>
                      <Select 
                        value={secondaryLanguage} 
                        onValueChange={setSecondaryLanguage}
                      >
                        <SelectTrigger data-testid="secondary-language-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {interviewLanguages
                            .filter(lang => lang.code !== primaryLanguage)
                            .map(lang => (
                              <SelectItem key={lang.code} value={lang.code}>
                                {lang.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-ink-muted italic">
                        {t('interviewLanguages.bilingualNote', "L'IA alternera entre les deux langues pendant l'entretien")}
                      </p>
                    </div>
                  )}
                </div>

                {/* Link Validity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 p-4 bg-canvas-paper rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-brand-primary" />
                      <Label className="text-sm font-medium">Validité du lien</Label>
                    </div>
                    <Select value={linkValidity} onValueChange={setLinkValidity}>
                      <SelectTrigger data-testid="link-validity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24h">24 heures</SelectItem>
                        <SelectItem value="7d">7 jours</SelectItem>
                        <SelectItem value="30d">30 jours</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-ink-muted">
                      Le lien expirera automatiquement après usage ou expiration
                    </p>
                  </div>
                </div>

                {/* Success message with link */}
                {createdLink && (
                  <div className="p-4 bg-functional-success/10 border border-functional-success/30 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 text-functional-success">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Lien créé avec succès !</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <input 
                        type="text" 
                        readOnly 
                        value={createdLink.interview_url || `${window.location.origin}/interview/${createdLink.token}`}
                        className="flex-1 px-3 py-2 bg-white border rounded text-sm"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(createdLink.interview_url || `${window.location.origin}/interview/${createdLink.token}`);
                          toast.success("Lien copié !");
                        }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {createdLink.emails_sent?.length > 0 && (
                      <div className="text-sm text-ink-muted">
                        <p className="font-medium text-ink mb-1">Emails envoyés à :</p>
                        {createdLink.emails_sent.map((email, idx) => (
                          <p key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-functional-success" />
                            {email.to}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <DialogFooter>
                  {createdLink ? (
                    <Button 
                      className="btn-primary" 
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                    >
                      Fermer
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button 
                        className="btn-primary" 
                        onClick={handleCreateLink}
                        disabled={!selectedJob || selectedCandidates.length === 0}
                        data-testid="create-link-submit-btn"
                      >
                        Créer le lien
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Links List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 skeleton rounded-sm" />
            ))}
          </div>
        ) : links.length === 0 ? (
          <Card className="card-flat">
            <CardContent className="py-16 text-center">
              <Link2 className="w-12 h-12 text-ink-muted mx-auto mb-4" />
              <h3 className="font-display font-semibold text-xl text-ink mb-2">
                {t('interviewLinks.noLinks', 'Aucun lien créé')}
              </h3>
              <p className="text-ink-muted mb-6">
                Créez votre premier lien d'entretien IA
              </p>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('interviewLinks.createLink', 'Créer un lien')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleLinkSort("job_title")}>
                    <span className="flex items-center gap-1">Poste<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">Candidat(s)</TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer select-none" onClick={() => toggleLinkSort("link_type")}>
                    <span className="flex items-center gap-1">Type<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="hidden md:table-cell cursor-pointer select-none" onClick={() => toggleLinkSort("expires_at")}>
                    <span className="flex items-center gap-1">Expiration<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleLinkSort("status")}>
                    <span className="flex items-center gap-1">Statut<ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLinks.map((link) => (
                  <TableRow key={link.link_id} className="table-row-hover" data-testid={`link-row-${link.link_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                          <Link2 className="w-5 h-5 text-brand-primary" />
                        </div>
                        <p className="font-medium text-ink">{getJobTitle(link.job_id)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="space-y-1">
                        {link.candidate_ids.slice(0, 2).map(id => (
                          <p key={id} className="text-sm">{getCandidateName(id)}</p>
                        ))}
                        {link.candidate_ids.length > 2 && (
                          <p className="text-xs text-ink-muted">+{link.candidate_ids.length - 2} autres</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline" className="capitalize">
                        {link.link_type === 'individual' ? (
                          <><User className="w-3 h-3 mr-1" /> Individuel</>
                        ) : (
                          <><Users className="w-3 h-3 mr-1" /> Groupe</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2 text-sm text-ink-muted">
                        <Clock className="w-4 h-4" />
                        {new Date(link.expires_at).toLocaleDateString('fr-FR')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={link.status === 'active' ? 'default' : 'secondary'}
                        className={
                          link.status === 'active' 
                            ? 'bg-functional-success/20 text-functional-success' 
                            : link.status === 'expired' 
                            ? 'bg-functional-error/20 text-functional-error'
                            : 'bg-zinc-200 text-zinc-600'
                        }
                      >
                        {link.status === 'active' ? 'Actif' : link.status === 'expired' ? 'Utilisé' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Copy link - only if active */}
                        {link.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyLink(link.token)}
                            title="Copier le lien"
                            data-testid={`copy-link-${link.link_id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Send email - only if active */}
                        {link.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => resendEmailToCandidate(link.link_id)}
                            title="Envoyer par email"
                            data-testid={`send-link-${link.link_id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Open link - only if active, show disabled state for expired */}
                        {link.status === 'active' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(`/interview/${link.token}`, '_blank')}
                            title="Ouvrir"
                            data-testid={`open-link-${link.link_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled
                            title="Lien expiré ou déjà utilisé"
                            className="opacity-50 cursor-not-allowed"
                          >
                            <ExternalLink className="w-4 h-4 text-zinc-400" />
                          </Button>
                        )}
                        {/* Delete link */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteLink(link.link_id)}
                          title="Supprimer"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-link-${link.link_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
